type APIResponse<T> = Promise<{ status: number; data: T }>;

const JWT_STORAGE_KEY = "jwt";

class APIClient {
  apiRoot: string;
  requestCount = 0;
  jwtToken: string | null = null;

  constructor(apiRoot: string) {
    this.apiRoot = apiRoot;
  }

  get<T>(path: string, query?: any): APIResponse<T> {
    return this.doRequest("get", path, query);
  }

  post<T>(path: string, body: any): APIResponse<T> {
    return this.doRequest("post", path, body);
  }

  put(path: string, body: any) {
    return this.doRequest("put", path, body);
  }

  delete(path: string, query?: any) {
    return this.doRequest("delete", path, query);
  }

  set token(token: string | null) {
    this.jwtToken = token;
    if (token) {
      localStorage.setItem(JWT_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(JWT_STORAGE_KEY);
    }
  }

  get token() {
    if (!this.jwtToken) {
      return localStorage.getItem(JWT_STORAGE_KEY);
    }
    return this.jwtToken;
  }

  private async doRequest(method: string, path: string, body?: any) {
    this.requestCount++;
    const headers: any = {
      "Content-Type": "application/json"
    };

    if (this.token) {
      headers["Authorization"] = "Bearer " + this.token;
    }

    let resp;

    try {
      resp = await fetch(this.apiRoot + path, {
        mode: "cors",
        credentials: "include",
        headers,
        method,
        body: body ? JSON.stringify(body) : undefined
      });

      this.requestCount--;

      return this.parseJsonResp(resp);
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  /**
   * Parses fetch response into json and handle any errors along the way.
   *
   * @param {Object} resp
   */
  private async parseJsonResp(resp: Response) {
    let jsonResp;

    if (resp.status === 204) {
      // Fake empty body if receiving 204 No Content
      return {};
    }

    if (resp.status === 403) {
      this.token = null;
    }

    try {
      jsonResp = await resp.json();
    } catch (err) {
      console.log("Failed parsing json response", err);

      // eslint-disable-next-line
      throw {
        status: 500,
        error: {
          code: "CLIENT_ERROR",
          detail: "Something went wrong when parsing json response on client: " + err
        }
      };
    }

    if (resp.ok) {
      return jsonResp;
    } else {
      throw jsonResp;
    }
  }
}

export default APIClient;
